/**
 * Analytics tracking utilities
 * Track user interactions, page views, and key metrics
 */
import { minimizeAnalyticsProperties } from './analytics-privacy';

export interface AnalyticsEvent {
  name: string;
  userId?: string;
  properties?: Record<string, any>;
  timestamp?: any;
}

let analyticsModulePromise: Promise<typeof import('firebase/analytics') | null> | null = null;
let firestoreModulePromise: Promise<
  [{ app: typeof import('./firebase').app; db: typeof import('./firebase').db }, typeof import('firebase/firestore')]
> | null = null;

function loadFirestoreModule() {
  if (!firestoreModulePromise) {
    firestoreModulePromise = Promise.all([import('./firebase'), import('firebase/firestore')]);
  }
  return firestoreModulePromise;
}

async function loadAnalyticsModule(): Promise<typeof import('firebase/analytics') | null> {
  if (!import.meta.env.PROD || typeof window === 'undefined') return null;
  if (!analyticsModulePromise) {
    analyticsModulePromise = import('firebase/analytics').catch((error) => {
      console.debug('[Analytics] Firebase Analytics SDK unavailable:', error);
      return null;
    });
  }
  return analyticsModulePromise;
}

function normalizeAnalyticsParams(properties?: Record<string, any>): Record<string, string | number> {
  if (!properties) return {};

  const normalized: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' || typeof value === 'number') {
      normalized[key] = value;
      continue;
    }
    if (typeof value === 'boolean') {
      normalized[key] = value ? 1 : 0;
      continue;
    }
    normalized[key] = String(value);
  }

  return normalized;
}

/**
 * Track an analytics event
 */
export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  const params = normalizeAnalyticsParams(minimizeAnalyticsProperties(event.name, event.properties));
  const analyticsSdk = await loadAnalyticsModule();
  if (analyticsSdk) {
    try {
      const { app } = await import('./firebase');
      const analytics = analyticsSdk.getAnalytics(app);
      analyticsSdk.logEvent(analytics, event.name, params as Record<string, string | number>);
    } catch (error) {
      console.debug('[Analytics] Failed to track GA4 event:', error);
    }
  }

  // Keep a privacy-minimized product counter for the SuperAdmin dashboard.
  // GA4 remains the source of truth; neither path receives a Firebase UID.
  if (import.meta.env.PROD) {
    try {
      const [{ db }, firestoreSdk] = await loadFirestoreModule();
      await firestoreSdk.addDoc(firestoreSdk.collection(db, 'analytics-events'), {
        name: event.name,
        eventDate: new Date().toISOString().slice(0, 10),
        properties: Object.fromEntries(Object.entries(params).slice(0, 12)),
        createdAt: firestoreSdk.serverTimestamp(),
        expiresAt: firestoreSdk.Timestamp.fromMillis(Date.now() + 30 * 86400000),
      });
    } catch (error) {
      console.debug('[Analytics] Failed to mirror product event:', error);
    }
  }
}

/**
 * Track page view
 */
export async function trackPageView(path: string, title?: string, userId?: string): Promise<void> {
  return trackEvent({
    name: 'page_view',
    userId,
    properties: { path, title },
  });
}

/**
 * Track search event
 */
export async function trackSearch(query: string, resultCount: number, userId?: string): Promise<void> {
  return trackEvent({
    name: 'search',
    userId,
    properties: { query, resultCount },
  });
}

/**
 * Track resource view
 */
export async function trackResourceView(
  resourceId: string,
  resourceName: string,
  resourceType: string,
  userId?: string,
): Promise<void> {
  return trackEvent({
    name: 'resource_view',
    userId,
    properties: { resourceId, resourceName, resourceType },
  });
}

/**
 * Track favorite toggle
 */
export async function trackFavoriteToggle(resourceId: string, isFavorited: boolean, userId?: string): Promise<void> {
  return trackEvent({
    name: 'favorite_toggle',
    userId,
    properties: { resourceId, isFavorited },
  });
}

/**
 * Track location save
 */
export async function trackLocationSave(city: string, state: string, userId?: string): Promise<void> {
  return trackEvent({
    name: 'location_saved',
    userId,
    properties: { city, state },
  });
}

/**
 * Track geolocation detection
 */
export async function trackGeolocationDetected(
  success: boolean,
  city?: string,
  state?: string,
  userId?: string,
): Promise<void> {
  return trackEvent({
    name: 'geolocation_detected',
    userId,
    properties: { success, city, state },
  });
}

/**
 * Track share action
 */
export async function trackShare(resourceId: string, method: string, userId?: string): Promise<void> {
  return trackEvent({
    name: 'resource_shared',
    userId,
    properties: { resourceId, method },
  });
}

/**
 * Initialize analytics tracking
 * Call once when app starts
 */
export function initializeAnalytics(userId?: string): void {
  if (typeof window === 'undefined') return;

  // Track clicks on key elements
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.dataset.analyticsEvent) {
      trackEvent({
        name: target.dataset.analyticsEvent,
        userId,
        properties: {
          elementId: target.id,
          elementClass: target.className,
        },
      });
    }
  });
}

/**
 * Get analytics summary for the last N days
 */
export async function getAnalyticsSummary(
  _days: number = 7,
): Promise<{ totalEvents: number; byEvent: Record<string, number> }> {
  try {
    // This would typically query Firestore with a date range
    // For now, return a placeholder
    return { totalEvents: 0, byEvent: {} };
  } catch (error) {
    console.error('[Analytics] Failed to get summary:', error);
    return { totalEvents: 0, byEvent: {} };
  }
}
